import { useEffect, useState } from "react";
import { api } from "../api.js";
import { useAuth } from "../auth.jsx";
import { ErrorBox, SuccessBox, fmtDate, CopyButton } from "../components/helpers.jsx";

export default function Admins() {
    const { me } = useAuth();
    const [rows, setRows] = useState([]);
    const [err, setErr] = useState(null);
    const [msg, setMsg] = useState(null);
    const [reload, setReload] = useState(0);

    const [newUser, setNewUser] = useState("");
    const [newPass, setNewPass] = useState("");
    const [newRole, setNewRole] = useState("admin");
    const [createdSecret, setCreatedSecret] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/api/admin/admins")
            .then((d) => { if (alive) setRows(d.rows); })
            .catch((e) => { if (alive) setErr(e.message); });
        return () => { alive = false; };
    }, [reload]);

    async function create(e) {
        e.preventDefault();
        setErr(null); setMsg(null); setCreatedSecret(null);
        try {
            const a = await api.post("/api/admin/admins", { username: newUser, password: newPass, role: newRole });
            setCreatedSecret(a);
            setNewUser(""); setNewPass(""); setNewRole("admin");
            setReload(Date.now());
        } catch (e) { setErr(e.message); }
    }

    async function disable(row, disabled) {
        try {
            await api.post(`/api/admin/admins/${row.id}/disabled`, { disabled });
            setMsg(`${row.username} ${disabled ? "disabled" : "enabled"}.`);
            setReload(Date.now());
        } catch (e) { setErr(e.message); }
    }

    async function rotateKey(row) {
        if (!confirm(`Rotate API key for ${row.username}? Any upstream server still using the old key will start getting 401s.`)) return;
        try {
            await api.post(`/api/admin/admins/${row.id}/api-key/rotate`);
            setMsg(`API key rotated for ${row.username}.`);
            setReload(Date.now());
        } catch (e) { setErr(e.message); }
    }

    async function setRoleFor(row, role) {
        if (row.id === me.id) { setErr("can't change your own role"); return; }
        try {
            await api.post(`/api/admin/admins/${row.id}/role`, { role });
            setMsg(`${row.username} is now ${role}.`);
            setReload(Date.now());
        } catch (e) { setErr(e.message); }
    }

    async function resetPass(row) {
        const p = prompt(`New password for ${row.username} (≥ 8 chars):`);
        if (!p) return;
        try {
            await api.post(`/api/admin/admins/${row.id}/password`, { password: p });
            setMsg(`Password reset for ${row.username}.`);
        } catch (e) { setErr(e.message); }
    }

    async function delAdmin(row) {
        if (row.id === me.id) { setErr("can't delete yourself"); return; }
        if (!confirm(`Delete ${row.username}? This wipes ALL their attempts, bans, hwid data. Irreversible.`)) return;
        try {
            await api.del(`/api/admin/admins/${row.id}`);
            setMsg(`${row.username} deleted.`);
            setReload(Date.now());
        } catch (e) { setErr(e.message); }
    }

    return (
        <>
            <div className="page-header">
                <div>
                    <h1>Admins</h1>
                    <div className="sub">Every tenant on this server. You're a super-admin so you can manage them all.</div>
                </div>
            </div>

            <ErrorBox>{err}</ErrorBox>
            <SuccessBox>{msg}</SuccessBox>

            {createdSecret && (
                <div className="card" style={{ borderColor: "var(--accent)", marginTop: 8 }}>
                    <h2>New admin created — save this</h2>
                    <table className="data"><tbody>
                        <tr><th>Username</th><td className="mono">{createdSecret.username}</td></tr>
                        <tr><th>Role</th><td><span className={`pill role${createdSecret.role === "super" ? " super" : ""}`}>{createdSecret.role}</span></td></tr>
                        <tr><th>API key</th><td>
                            <div className="copy-row">
                                <div className="mono">{createdSecret.apiKey}</div>
                                <CopyButton text={createdSecret.apiKey} />
                            </div>
                        </td></tr>
                    </tbody></table>
                    <div className="text-dim" style={{ marginTop: 8, fontSize: 12 }}>
                        Tell them to log in and change the password. They can rotate the key from their own panel.
                    </div>
                </div>
            )}

            <div className="card" style={{ marginTop: 12 }}>
                <h2>Create admin</h2>
                <form onSubmit={create} className="row" style={{ marginTop: 6 }}>
                    <input placeholder="username" value={newUser} onChange={(e) => setNewUser(e.target.value)} style={{ flex: 1 }} />
                    <input placeholder="password (≥8 chars)" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} style={{ flex: 1 }} />
                    <select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                        <option value="admin">admin</option>
                        <option value="super">super</option>
                    </select>
                    <button className="primary" type="submit" disabled={!newUser || !newPass}>Create</button>
                </form>
            </div>

            <div className="table-wrap">
                <table className="data">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>API key</th>
                            <th>Key rotated</th>
                            <th>Created</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td className="mono">{r.username}{r.id === me.id && <span className="text-dim"> (you)</span>}</td>
                                <td>
                                    <span className={`pill role${r.role === "super" ? " super" : ""}`}>{r.role}</span>
                                    {r.id !== me.id && (
                                        <button className="ghost" style={{ marginLeft: 6 }}
                                                onClick={() => setRoleFor(r, r.role === "super" ? "admin" : "super")}>
                                            {r.role === "super" ? "→ admin" : "→ super"}
                                        </button>
                                    )}
                                </td>
                                <td>
                                    {r.disabledAt
                                        ? <span className="pill active">disabled</span>
                                        : <span className="pill lifted">active</span>}
                                </td>
                                <td className="mono" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.apiKey}>
                                    {r.apiKey.slice(0, 10)}…
                                    <CopyButton text={r.apiKey} />
                                </td>
                                <td title={new Date(r.apiKeyCreatedAt).toLocaleString()}>{fmtDate(r.apiKeyCreatedAt)}</td>
                                <td>{fmtDate(r.createdAt)}</td>
                                <td>
                                    <div className="row">
                                        <button className="ghost" onClick={() => rotateKey(r)}>rotate key</button>
                                        <button className="ghost" onClick={() => resetPass(r)}>reset pass</button>
                                        {r.id !== me.id && (
                                            <button className="ghost" onClick={() => disable(r, !r.disabledAt)}>
                                                {r.disabledAt ? "enable" : "disable"}
                                            </button>
                                        )}
                                        {r.id !== me.id && (
                                            <button className="danger" onClick={() => delAdmin(r)}>delete</button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}
